import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { ComposerFormValues } from '../schema';
import { AttachmentGrid, AttachmentTile } from '~/components/email-attachments';

export function FileAttachments() {
  const { control, formState, getValues, setValue } =
    useFormContext<ComposerFormValues>();
  const attachments = useWatch({ control, name: 'attachments' });

  function removeAttachment(index: number) {
    setValue(
      'attachments',
      getValues('attachments').filter(
        (_, currentIndex) => currentIndex !== index,
      ),
      /* Revalidate, like every other path that changes the attachment set:
         dropping a file is how you get back under the limit, so leaving the
         error it caused on screen tells you the fix did not work. */
      { shouldDirty: true, shouldValidate: true },
    );
  }

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 shrink-0">
      <AttachmentGrid>
        {attachments.map((file, index) => (
          <FileTile
            key={`${file.name}:${index}`}
            file={file}
            disabled={formState.isSubmitting}
            onRemove={() => removeAttachment(index)}
          />
        ))}
      </AttachmentGrid>
    </div>
  );
}

/** The tile a sent mail shows, for a file that is not a mail's yet. */
function FileTile({
  disabled,
  file,
  onRemove,
}: {
  disabled: boolean;
  file: File;
  onRemove: () => void;
}) {
  const url = useObjectUrl(file);

  return (
    <AttachmentTile
      attachment={{
        contentType: file.type,
        filename: file.name,
        size: file.size,
        url,
      }}
      onRemove={onRemove}
      removeDisabled={disabled}
    />
  );
}

/**
 * A picked file has nowhere to point until it is uploaded, so the tile is
 * pointed at the bytes already in the browser and previews them like any other.
 *
 * Keyed on the `File` itself, which form state holds by reference and never
 * copies, so the handle survives every re-render and is revoked exactly once.
 */
function useObjectUrl(file: File) {
  const [url, setUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    const created = URL.createObjectURL(file);
    // The handle exists only once the browser has made it: nothing to derive
    // during render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUrl(created);

    return () => URL.revokeObjectURL(created);
  }, [file]);

  return url;
}
